import { Lightning } from '@lightningjs/sdk';
import LoadingSpinner from './LoadingSpinner';
import { getImageTextureObj } from 'support/generalUtils';
import QRCode, { QRCodeRenderersOptions } from 'qrcode';

export interface QrCodeTemplateSpec extends Lightning.Component.TemplateSpec {
  width: number;
  height: number;
  onLoad?: () => void;
  onError?: () => void;
  QrImage: Lightning.textures.ImageTexture;
  LoadingSpinner: typeof LoadingSpinner;
}

const DEFAULT_SIZE = 208;
const DEFAULT_QR_OPTIONS: QRCodeRenderersOptions = {
  margin: 2,
  scale: 4,
};

export default class QrCode
  extends Lightning.Component<QrCodeTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<QrCodeTemplateSpec>
{
  private _onError: QrCodeTemplateSpec['onError'];
  private _onLoad: QrCodeTemplateSpec['onLoad'];
  private _width = DEFAULT_SIZE;
  private _height = DEFAULT_SIZE;

  private _QrImage = this.getByRef('QrImage')!;
  private _LoadingSpinner = this.getByRef('LoadingSpinner')!;

  get onError() {
    return this._onError;
  }

  set onError(onError: QrCodeTemplateSpec['onError']) {
    this._onError = onError;
  }

  get onLoad() {
    return this._onLoad;
  }

  set onLoad(onLoad: QrCodeTemplateSpec['onLoad']) {
    this._onLoad = onLoad;
  }

  get width() {
    return this._width;
  }

  set width(value: number) {
    this._width = value;
    this.updateLayout();
  }

  get height() {
    return this._height;
  }

  set height(value: number) {
    this._height = value;
    this.updateLayout();
  }

  static override _template(): Lightning.Component.Template<QrCodeTemplateSpec> {
    return {
      QrImage: {},
      LoadingSpinner: {
        type: LoadingSpinner,
        show: false,
      },
    };
  }

  override _active() {
    this._QrImage.on('txLoaded', () => {
      this._LoadingSpinner.show = false;
      this.onLoad?.();
    });
    this._QrImage.on('txError', () => {
      this._LoadingSpinner.show = false;
      this.onError?.();
    });

    this.updateLayout();
    this._LoadingSpinner.show = true;
  }

  override _inactive() {
    this._QrImage.removeAllListeners('txLoaded');
    this._QrImage.removeAllListeners('txError');
  }

  private updateLayout() {
    this.patch({ w: this.width, h: this.height });
    this._QrImage.patch({ w: this.width, h: this.height });
    this._LoadingSpinner.patch({ x: this.width / 2, y: this.height / 2 });
  }

  private generateQrDataUrl = async (
    text: string,
    options?: QRCodeRenderersOptions,
  ) => {
    try {
      return await QRCode.toDataURL(text, options);
    } catch (err) {
      this.onError?.();
    }
  };

  private async fetchQrCode(content: string, options?: QRCodeRenderersOptions) {
    const link = await this.generateQrDataUrl(content, options);

    if (link && link !== this._QrImage.src) {
      this._QrImage.patch(getImageTextureObj(link, this.width, this.height));
    }
  }

  load(content: string, options: QRCodeRenderersOptions = DEFAULT_QR_OPTIONS) {
    this.fetchQrCode(content, options);
  }
}
